import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { and, not, or } from 'ramda';
import PropTypes from 'prop-types';
import GranteesForm from './components/GranteesForm/GranteesForm';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer';
import useSimulationCustomerGranteesForm from './hooks/useSimulationCustomerGranteesForm.js';
import CustomerForm from './components/CustomerForm/CustomerForm';
import { Button, Icon, PageTitle, Tabs, Tooltip } from '@fasstech/middle-design-system';
import { useMemo } from 'react';
import useHandleGrantees from './hooks/useHandleGrantees.js';
import { formatProject } from '@@viewHelpers/format.jsx';
import { PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import { useTabs } from '@@viewCommonComponents/hooks/useTabs.js';
import TabsPagination from '@@viewCommonComponents/TabsPagination.jsx';
import ExtraUnauthorizedModal from '@@viewCommonComponents/displayer/ExtraUnauthorizedModal';
import useTelEmailValidation from '@@viewCommonHooks/useTelEmailValidation.js';

const SimulationCustomerGrantees = ({
  jobs,
  regimeObligatoire,
  professionalSituations,
  socioProfessionalCategories,
  activities,
  getContract: project,
  middleFeatures,
  getMiddleText,
  getMiddleBoolean,
  getMiddleNumber,
  phoneIndicatives
}) => {
  const defaultValues = useMemo(() => formatProject(project), [project]);

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    shouldUnregister: false,
    defaultValues
  });

  const { watch, control } = form;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'grantees',
    keyName: 'uuid'
  });

  const customer = watch('customer');

  const grantees = getMiddleBoolean('BENEFICIAIRE_DONNEES_CONTRAT_EST_NECESSAIRE')
    ? [customer, ...fields]
    : [customer];

  const { tabs, setTabs, updateVisibility, isButtonDisabled, displayTabsPagination } = useTabs({
    grantees,
    getMiddleText,
    filteredViewMode: !getMiddleBoolean('BENEFICIAIRE_DONNEES_CONTRAT_EST_NECESSAIRE')
  });

  const {
    onSubmit,
    unauthorizedModalMessage,
    setUnauthorizedModalMessage,
    isUnauthorized,
    isFetchingForm,
    postalCodeFranceMetro,
    postalCodeDROM,
    $onPrevClick,
    isValid,
    displayAdditionalInfo,
    displayActivityInfo
  } = useSimulationCustomerGranteesForm({
    jobs,
    professionalSituations,
    form,
    getMiddleBoolean,
    middleFeatures,
    getMiddleText
  });

  const nbMaxChild = getMiddleNumber('BENEFICIAIRE_TYPE_NB_MAX_CHILD');

  const {
    handleRemoveGrantee,
    displayDeletionModal,
    setDisplayDeletionModal,
    deletingGrantee,
    setDeletingGrantee,
    isDisabledAddingChild,
    isDisabledAddingSpouse,
    isDisabledAddBeneficiary,
    handleAddBeneficiary
  } = useHandleGrantees({
    setTabs,
    nbMaxChild: getMiddleText('BENEFICIAIRE_TYPE_CHILD_LABEL')
      ? getMiddleNumber('BENEFICIAIRE_TYPE_NB_MAX_CHILD')
      : 0,
    fields,
    append,
    remove,
    nbMaxSpouse: getMiddleText('BENEFICIAIRE_TYPE_SPOUSE_LABEL')
      ? getMiddleNumber('BENEFICIAIRE_TYPE_NB_MAX_SPOUSE')
      : 0,
    form
  });

  const { telEmailValidationHasError, telEmailValidationErrorMessage } = useTelEmailValidation(
    form,
    getMiddleBoolean,
    true
  );

  return (
    <FormProvider {...form}>
      <form className={'w-full'} onSubmit={form.handleSubmit(onSubmit)}>
        <PageLayout>
          <Icon
            name={tabs.selectedTab === 0 ? 'informations_adherent' : 'informations_beneficiaire'}
            size={70}
          />
          <PageTitle>
            {getMiddleText(
              tabs.selectedTab === 0
                ? 'ADHERENT_GENERAL_VIEW_TITLE'
                : 'BENEFICIAIRE_LABEL_GENERAL_VIEW_TITLE'
            )}
          </PageTitle>

          <div className={'flex flex-col gap-4'}>
            <TabsPagination
              updateVisibility={updateVisibility}
              isButtonDisabled={isButtonDisabled}
              show={displayTabsPagination}
            >
              <Tabs
                items={tabs.tabs}
                selected={tabs.selectedTab}
                onClick={(index) =>
                  setTabs({
                    selectedTab: index,
                    tabs: tabs.tabs
                  })
                }
                isDisplayed={tabs && tabs.tabs.length > 1}
              />
            </TabsPagination>
          </div>
          <CustomerForm
            getMiddleText={getMiddleText}
            getMiddleBoolean={getMiddleBoolean}
            getMiddleNumber={getMiddleNumber}
            postalCodeFranceMetro={postalCodeFranceMetro}
            postalCodeDROM={postalCodeDROM}
            professionalSituations={professionalSituations}
            socioProfessionalCategories={socioProfessionalCategories}
            activities={activities}
            regimeObligatoire={regimeObligatoire}
            phoneIndicatives={phoneIndicatives}
            jobs={jobs}
            selectedTab={tabs.selectedTab}
            displayAdditionalInfo={displayAdditionalInfo}
            displayActivityInfo={displayActivityInfo}
            telEmailValidationHasError={telEmailValidationHasError}
            telEmailValidationErrorMessage={telEmailValidationErrorMessage}
          />

          {getMiddleBoolean('BENEFICIAIRE_DONNEES_CONTRAT_EST_NECESSAIRE') && (
            <GranteesForm
              professionalSituations={professionalSituations}
              regimeObligatoire={regimeObligatoire}
              getMiddleText={getMiddleText}
              getMiddleBoolean={getMiddleBoolean}
              getMiddleNumber={getMiddleNumber}
              selectedTab={tabs.selectedTab}
              fields={fields}
              handleRemoveGrantee={handleRemoveGrantee}
              displayDeletionModal={displayDeletionModal}
              setDisplayDeletionModal={setDisplayDeletionModal}
              deletingGrantee={deletingGrantee}
              setDeletingGrantee={setDeletingGrantee}
              isDisabledAddingChild={isDisabledAddingChild}
              isDisabledAddingSpouse={isDisabledAddingSpouse}
            />
          )}
        </PageLayout>
        <ButtonContainer
          $onPreviousButtonClick={$onPrevClick}
          nextButtonLabel={getMiddleText('GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL')}
          nextButtonIsDisabled={
            isFetchingForm || isUnauthorized || not(isValid) || telEmailValidationHasError
          }
          isFormError={not(isValid) || telEmailValidationHasError}
          getMiddleText={getMiddleText}
          displayResumeButton>
          {getMiddleBoolean('BENEFICIAIRE_DONNEES_CONTRAT_EST_NECESSAIRE') && (
            <div className={'flex gap-4 whitespace-nowrap'}>
              <Tooltip
                isDisplayed={isDisabledAddBeneficiary}
                text={getMiddleText('BENEFICIAIRE_TYPE_MAX_CHILD_ATTEINT', {
                  nbMaxChild: nbMaxChild
                })}>
                <Button
                  variant="secondary"
                  isFormError={and(not(isValid), not(isDisabledAddBeneficiary))}
                  onClick={handleAddBeneficiary}
                  disabled={or(isDisabledAddBeneficiary, isUnauthorized) || !isValid}
                  data-testid="BENEFICIAIRE_AJOUT_LIEN"
                  className={'w-full'}>
                  {getMiddleText('BENEFICIAIRE_LABEL_AJOUT_LIEN')}
                </Button>
              </Tooltip>
            </div>
          )}
        </ButtonContainer>
      </form>
      <ExtraUnauthorizedModal
        isDisplayed={unauthorizedModalMessage}
        unauthorizedModalMessage={unauthorizedModalMessage}
        setUnauthorizedModalMessage={setUnauthorizedModalMessage}
      />
    </FormProvider>
  );
};

SimulationCustomerGrantees.propTypes = {
  jobs: PropTypes.array,
  regimeObligatoire: PropTypes.object,
  professionalSituations: PropTypes.array,
  socioProfessionalCategories: PropTypes.array,
  activities: PropTypes.array,
  getContract: PropTypes.object,
  middleFeatures: PropTypes.object,
  getMiddleText: PropTypes.func,
  getMiddleBoolean: PropTypes.func,
  getMiddleNumber: PropTypes.func
};

export default SimulationCustomerGrantees;
